/** @jsxImportSource @compiled/react */
import React from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- React must be in scope for JSX in Jest
void React;

import { defineMessages, useIntl } from 'react-intl';
import { css, cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';

import { ContributorCard } from './ContributorCard.tsx';
import { useSpaceContributors } from './useSpaceContributors.ts';

const i18n = defineMessages({
	empty: {
		id: 'space-contributors.list.empty',
		defaultMessage: 'No contributors found for this space.',
		description: 'Message shown when no contributors are found',
	},
	loading: {
		id: 'space-contributors.list.loading',
		defaultMessage: 'Loading contributors…',
		description: 'Loading label for the contributors list spinner',
	},
});

export interface SpaceContributorsListProps {
	spaceKey: string;
}

const styles = cssMap({
	container: {},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		paddingBlock: token('space.400'),
	},
});

const gridStyles = css({
	display: 'grid',
	gridTemplateColumns: 'repeat(2, 1fr)',
	gap: token('space.200'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries -- container query needed to respond to content area width rather than viewport
	'@container (min-width: 700px)': { gridTemplateColumns: 'repeat(3, 1fr)' },
});

const SpaceContributorsListComponent = ({ spaceKey }: SpaceContributorsListProps) => {
	const intl = useIntl();
	const { contributors, loading, error } = useSpaceContributors(spaceKey);

	if (loading) {
		return (
			<Box xcss={styles.loadingContainer} testId="space-contributors-loading">
				<Spinner size="medium" label={intl.formatMessage(i18n.loading)} />
			</Box>
		);
	}

	if (error) {
		return null;
	}

	if (contributors.length === 0) {
		return (
			<Box xcss={styles.container} testId="space-contributors-empty">
				<Text>{intl.formatMessage(i18n.empty)}</Text>
			</Box>
		);
	}

	return (
		<Box xcss={styles.container} testId="space-contributors-list">
			<Stack space="space.200">
				<div css={gridStyles} data-testid="space-contributors-grid">
					{contributors.map((contributor) => (
						<ContributorCard key={contributor.accountId} contributor={contributor} />
					))}
				</div>
			</Stack>
		</Box>
	);
};

const renderErrorFallback = () => null;

export const SpaceContributorsList = (props: SpaceContributorsListProps) => (
	<GenericErrorBoundary renderOnError={renderErrorFallback} attribution={Attribution.DISCO}>
		<SpaceContributorsListComponent {...props} />
	</GenericErrorBoundary>
);
